import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { AuthService } from 'src/app/services/auth.service';
import { NotificationService } from 'src/app/services/notification.service';
import { environment } from 'src/environments/environment';
import { CerereSabloaneService } from '../cerere-sabloane.service';
import { catchError, distinctUntilChanged, finalize, map, Observable, of, Subject, switchMap, takeUntil, tap } from 'rxjs';
import { AngularEditorConfig } from '@kolkov/angular-editor';
import { initializeEditor } from 'src/app/utils/editorConfig';
import { TemplateCategoriesService } from '../../template-categories/template-categories.service';
import { helper } from 'src/environments/helper';
import { notDefaultValidator } from 'src/app/validators/not-default';
import { Generic } from 'src/app/interfaces/generic.interface';
import { CommonService } from 'src/app/services/common.service';

@Component({
    selector: 'app-add-cerere-sabloane',
    templateUrl: './add-cerere-sabloane.component.html',
    styleUrls: ['./add-cerere-sabloane.component.scss'],
})
export class AddCerereSabloaneComponent implements OnInit, OnDestroy {
    // general error
    errorTitle: string = environment.config.customNotifications.headers.error;
    errorIcon: string = environment.config.customNotifications.icons.error;
    errorType: string = environment.config.customNotifications.icons.error;
    helper = helper;

    editorConfig!: AngularEditorConfig;

    token!: string;
    isLoading = false;
    form: FormGroup = new FormGroup({
        name: new FormControl('', [Validators.required]),
        content: new FormControl('', [Validators.required]),
        directie_id: new FormControl('', [Validators.required]),
        compartiment_id: new FormControl(''),
        primary_category_id: new FormControl('')
    });

    availableCategories: any[] = [];

    directiiActive: Generic[] = this.data;
    compartimenteActive!: Generic[];

    private addDestroy$ = new Subject<void>();

    filteredDirectii: any[] = this.data;
    directiiControl = new FormControl();

    filteredCompartimente: any[] = [];
    compartimenteControl = new FormControl();

    constructor(
        private dialogRef: MatDialogRef<AddCerereSabloaneComponent>,
        @Inject(MAT_DIALOG_DATA) public data: any,
        private cerereSabloaneService: CerereSabloaneService,
        private notificationService: NotificationService,
        private authService: AuthService,
        private templateCategoriesService: TemplateCategoriesService,
        private commonService: CommonService
    ) {
        this.processToken();
    }

    ngOnInit(): void {
        this.editorConfig = initializeEditor();
        this.renderFieldsSearch();
    }

    ngOnDestroy(): void {
        this.addDestroy$.next();
        this.addDestroy$.complete();
    }

    closeDialog() {
        this.dialogRef?.close({
            success: false,
        });
    }

    processToken() {
        const tokenObj = this.authService.returnTokenUser();
        tokenObj.then((token) => {
            this.token = token;
            this.cerereSabloaneService.setToken(token);
        });
    }

    renderFieldsSearch() {
        this.directiiControl.valueChanges.pipe(
            takeUntil(this.addDestroy$),
            distinctUntilChanged(),
            switchMap((inputValue) => {
                if (inputValue.length < 1) {
                    this.filteredDirectii = this.directiiActive;
                    return of(this.directiiActive);
                }
                return this.filterDirectii(inputValue);
            }),
            tap((data: any[]) => {
                this.filteredDirectii = data;
            }),
            catchError(() => of([]))
        ).subscribe();

        this.compartimenteControl.valueChanges.pipe(
            takeUntil(this.addDestroy$),
            distinctUntilChanged(),
            switchMap((inputValue) => {
                if (inputValue.length < 1) {
                    this.filteredCompartimente = this.compartimenteActive;
                    return of(this.compartimenteActive);
                }
                return this.filterCompartimente(inputValue);
            }),
            tap((data: any[]) => {
                this.filteredCompartimente = data;
            }),
            catchError(() => of([]))
        ).subscribe();
    }

    normalizeString(value: string): string {
        return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
    }

    filterDirectii(searchTerm: string): Observable<any[]> {
        const normalizedSearchTerm = this.normalizeString(searchTerm);

        const filtered = this.directiiActive.filter(directie =>
            this.normalizeString(directie.name).includes(normalizedSearchTerm)
        );

        return of(filtered);
    }

    onDirectieSelectClosed() {
        const selectedValue = this.directiiControl.value;

        if (selectedValue) {
            const isSelectedInFilteredList = this.filteredDirectii.some(directie =>
                directie.id === this.form.get('directie_id')?.value
            );

            if (!isSelectedInFilteredList) {
                this.directiiControl.setValue(null);
                this.filteredDirectii = this.directiiActive;
                this.addDestroy$.next();
                this.addDestroy$.complete();
                this.renderFieldsSearch();
            }
        }
    }

    onDirectieChange(directieId: number): void {
        this.form.controls['compartiment_id'].patchValue(-1);

        if (directieId == -1) {
            this.compartimenteActive = [];
            this.filteredCompartimente = [];
            return;
        }

        if (directieId) {
            this.loadCompartimenteActive(directieId);
        }
    }

    loadCompartimenteActive(directieId?: number) {
        this.isLoading = true;
        let compartimenteObservable$ = directieId
            ? this.commonService.getCompartimenteByDirectieId(directieId)
            : this.commonService.getCompartimenteActive();

        compartimenteObservable$.pipe(
            map((results: any) => [...results]),
            finalize(() => (this.isLoading = false))
        ).subscribe({
            next: (res) => {
                this.compartimenteActive = [...res];
                this.filteredCompartimente = [...res];
            },
            error: (err) => {
                console.log(err);
                this.filteredCompartimente = [];
            },
        })
    }

    filterCompartimente(searchTerm: string): Observable<any[]> {
        const normalizedSearchTerm = this.normalizeString(searchTerm);

        const filtered = this.compartimenteActive.filter(compartiment =>
            this.normalizeString(compartiment.name).includes(normalizedSearchTerm)
        );

        return of(filtered);
    }

    onCompartimentSelectClosed() {
        const selectedValue = this.compartimenteControl.value;

        if (selectedValue) {
            const isSelectedInFilteredList = this.filteredCompartimente.some(compartiment =>
                compartiment.id === this.form.get('compartiment_id')?.value
            );

            if (!isSelectedInFilteredList) {
                this.compartimenteControl.setValue(null);
                this.filteredCompartimente = this.compartimenteActive;
                this.addDestroy$.next();
                this.addDestroy$.complete();
                this.renderFieldsSearch();
            }
        }
    }

    submitForm() {
        this.isLoading = true;

        const sablon = {
            name: this.form.get('name')?.value,
            content: this.form.get('content')?.value,
            directie_id: this.form.get('directie_id')?.value || undefined,
            compartiment_id: this.form.get('compartiment_id')?.value || undefined,
            primary_category_id: this.form.get('primary_category_id')?.value || undefined,
        };

        this.cerereSabloaneService
            .addSablon(sablon)
            .pipe(finalize(() => (this.isLoading = false)))
            .subscribe({
                next: (res) => {
                    this.dialogRef.close({ success: true });
                },
                error: (err) => {
                    let msg;
                    try {
                        // try to catch the message from server
                        msg = err.error.errors.message[0];
                    } catch (error) {
                        // controll output message
                        msg = helper.dialogConfig.generalMessages.error;
                    }
                    this.notificationService.warningSwal(
                        this.errorTitle,
                        msg,
                        this.errorIcon
                    );
                },
            });
    }

    onCompartimentChange(): void {
        const compartimentId = this.form.get('compartiment_id')?.value;

        // Reset primary category when compartiment changes
        this.form.patchValue({
            primary_category_id: ''
        });

        this.availableCategories = [{ id: '', name: '-- Toate --' }];

        this.loadAvailableCategories();
    }

    loadAvailableCategories(): void {
        const directieId = this.form.get('directie_id')?.value;
        const compartimentId = this.form.get('compartiment_id')?.value;

        if (!directieId) {
            this.availableCategories = [{ id: '', name: '-- Toate --' }];
            return;
        }

        this.isLoading = true;
        this.templateCategoriesService.setToken(this.token);
        this.templateCategoriesService.getCategoriesByOrganization(parseInt(directieId), compartimentId ? parseInt(compartimentId) : undefined)
            .pipe(finalize(() => (this.isLoading = false)))
            .subscribe({
                next: (response) => {
                    if (response.errors === false) {
                        this.availableCategories = [
                            { id: '', name: '-- Toate --' },
                            ...(response.data || [])
                        ];
                    }
                },
                error: (error) => {
                    console.error('Error loading categories:', error);
                    this.availableCategories = [
                        { id: '', name: '-- Toate --' }
                    ];
                }
            });
    }
}
